import { Decorative } from "@faire/design-tokens";
import { trackSignupModalOpenPdpMainCtaClick as trackSignupPdpMainCtaClick } from "@faire/web-api/events/signup/click/modalOpenPdpMainCta";
import { IBadge } from "@faire/web-api/indigofair/badges/IBadge";
import { IBrandPromoCollection } from "@faire/web-api/indigofair/data/IBrandPromoCollection";
import { IClickInfo } from "@faire/web-api/indigofair/data/IClickInfo";
import { IDisclaimer } from "@faire/web-api/indigofair/data/IDisclaimer";
import { IInformationMessage } from "@faire/web-api/indigofair/data/IInformationMessage";
import { IInsiderLabel } from "@faire/web-api/indigofair/data/IInsiderLabel";
import { IProduct } from "@faire/web-api/indigofair/data/IProduct";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { IInsiderModalRequest } from "@faire/web-api/indigofair/insider/IInsiderModalRequest";
import { useStrictLocalization } from "@faire/web/common/localization";
import { getCurrentPath } from "@faire/web/common/Path";
import { Button } from "@faire/web/slate/Button";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { EmbeddedLink } from "@faire/web/ui/EmbeddedLink";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React, { useEffect, useMemo } from "react";
import styled from "styled-components";

import { InsiderLabel } from "@faire/retailer/components/InsiderLabel/InsiderLabel";
import { ISamplesBannerDetails } from "@faire/retailer/components/Modals/SamplesRequestModal/RequestSamplesCTA";
import { ShowroomBadge } from "@faire/retailer/components/Showroom/ShowroomBadge";
import { TrackedLinkContext } from "@faire/retailer/components/TrackedLink";
import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useFaireDirectSignUp } from "@faire/retailer/hooks/FaireDirect/useFaireDirectSignUp/useFaireDirectSignUp";
import { IReviewSummary } from "@faire/retailer/lib/buildReviewSummary";
import { isLoggedInRetailerOrBrandPreview } from "@faire/retailer/lib/isLoggedInRetailer";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer/lib/popSignUp";
import { getSettingEnableRetailerExclusivityAgreements } from "@faire/retailer/settings/getSettingEnableRetailerExclusivityAgreement";
import { useSettingFaireDirectPDPSignup } from "@faire/retailer/settings/useSettingFaireDirectPDPSignup";
import {
  useAssignSettingOwFImproveTermsTouchpointsPDPShipping,
  useSettingOwFImproveTermsTouchpointsPDPShipping,
} from "@faire/retailer/settings/useSettingOwFImproveTermsTouchpointsPDPShipping";
import { AddToCartVariants } from "@faire/retailer/stores/domain/CartStore/triggerAddToCartEvent";
import { useMarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";
import { getProductSubheaderName } from "@faire/retailer/views/product/__internal__/SEOMetaText";
import {
  getProductDetails,
  getProductOptions,
} from "@faire/retailer/views/product/utils/productUtils";

import { ProductPromoBanners } from "./__internal__/ProductPromoBanners/ProductPromoBanners";
import { Disclaimer } from "./AddToCart/Disclaimer";
import { ProductAddToCart } from "./AddToCart/ProductAddToCart";
import { ProductAddToCartState } from "./AddToCart/ProductAddToCartState";
import { ExclusivityLabel } from "./ExclusivityLabel";
import { ProductDetailsHeader } from "./Header";
import { LoggedInOnlyNote } from "./LoggedInOnlyNote";
import { ShippingDetailsList } from "./ShippingDetailsList";

interface IProps {
  className?: string;
  brand: IProductPageResponse.IPdpBrand;
  product: IProductPageResponse.IPdpProduct;
  productAddToCartState: ProductAddToCartState;
  brandReviewSummary: IReviewSummary | undefined;
  importShipmentInfo?: string;
  infoTexts?: IInformationMessage[];
  showElevatedMPPProductExperience: boolean;
  disclaimers?: IDisclaimer[];
  preselectedProductOptionToken?: string;
  badges?: IBadge[] | undefined;
  sampleDetails?: ISamplesBannerDetails;
  afterAddToCart?: () => void;
  showSideCart?: boolean;
}

const ProductDetailsBase: React.FC<IProps> = ({
  className,
  brand,
  product,
  productAddToCartState,
  brandReviewSummary,
  importShipmentInfo,
  infoTexts,
  showElevatedMPPProductExperience,
  disclaimers,
  preselectedProductOptionToken,
  badges,
  sampleDetails,
  afterAddToCart,
  showSideCart,
}) => {
  const viewport = useViewport();
  const settingOwFImproveTermsTouchpointsPDPShipping =
    useSettingOwFImproveTermsTouchpointsPDPShipping();
  const assignSettingOwFImproveTermsTouchpointsPDPShipping =
    useAssignSettingOwFImproveTermsTouchpointsPDPShipping();

  useEffect(() => {
    assignSettingOwFImproveTermsTouchpointsPDPShipping();
  }, [assignSettingOwFImproveTermsTouchpointsPDPShipping]);

  const brandPromotion = useBrandPromotion(brand, product);

  const showAddToCartCTA =
    product.state === IProduct.State.FOR_SALE ||
    getProductOptions(product).length > 0;

  const nonReturnableReason =
    product.purchase_information?.non_returnable_reason_data?.default_message;

  const promotionLabelText =
    productAddToCartState.productPromotionState?.promotionLabel?.text;

  const insiderLabel =
    productAddToCartState.productPromotionState?.insiderLabel;

  const showShowroomBadge =
    showElevatedMPPProductExperience && viewport.isTabletAndBelow;

  const safetyInformation = disclaimers?.filter(
    (disclaimer) => disclaimer.type === IDisclaimer.Type.FACE_MASK
  );

  const otherDisclaimers = disclaimers?.filter(
    (disclaimer) => disclaimer.type !== IDisclaimer.Type.FACE_MASK
  );

  return (
    <Wrapper className={className}>
      {showShowroomBadge ? <StyledShowroomBadge /> : null}
      <ProductDetailsHeader
        brand={brand}
        product={getProductDetails(product)}
        selectedOption={productAddToCartState.selectedOption}
        brandReviewSummary={brandReviewSummary}
        productPromotionsState={productAddToCartState.productPromotionState}
        perStyleOptionVariations={
          productAddToCartState.perStyleOptionVariations
        }
        brandPromotion={brandPromotion}
        badges={badges}
        showOutOfStockUnderProductName={false}
        hideBrandDetails={viewport.isDesktopAndAbove}
      />
      {isLoggedInRetailerOrBrandPreview() ? (
        <Spacer height="3x" />
      ) : (
        <SignUpEntryPoint
          productToken={product.token}
          brandToken={brand.token}
        />
      )}
      {
        // Inactive products are not guaranteed to have product options,
        // if they don't, we do not render the add to cart view.
        // Online only restricted products are never purchasable, so we do not render add to cart
      }
      {showAddToCartCTA ? (
        <TrackedLinkContext.Provider
          value={{
            type: IClickInfo.Type.PRODUCT,
            containerName: "product_page",
          }}
        >
          <ProductAddToCart
            brand={brand}
            product={product}
            productAddToCartState={productAddToCartState}
            preselectedProductOptionToken={preselectedProductOptionToken}
            allowCustomOption={!viewport.isMobile}
            sampleDetails={sampleDetails}
            importShipmentInfo={importShipmentInfo}
            infoTexts={infoTexts}
            variant={AddToCartVariants.PDP_ADD_BTN}
            afterAddToCart={afterAddToCart}
            showSideCart={showSideCart}
          />
        </TrackedLinkContext.Provider>
      ) : null}
      <Subheader brandCompanyName={brand.name} />
      {settingOwFImproveTermsTouchpointsPDPShipping ? (
        <BelowAddToCartDetailsUpdate
          nonReturnableReason={nonReturnableReason}
          brand={brand}
          importShipmentInfo={importShipmentInfo}
          infoTexts={infoTexts}
          safetyInformation={safetyInformation}
          promotionLabelText={promotionLabelText}
          productAddToCartState={productAddToCartState}
          product={product}
        />
      ) : (
        <BelowAddToCartDetails
          nonReturnableReason={nonReturnableReason}
          brand={brand}
          importShipmentInfo={importShipmentInfo}
          infoTexts={infoTexts}
          safetyInformation={safetyInformation}
          promotionLabelText={promotionLabelText}
          insiderLabel={insiderLabel}
          productAddToCartState={productAddToCartState}
          product={product}
        />
      )}
      <Disclaimers disclaimers={otherDisclaimers} />
      <Spacer size="2x" />
    </Wrapper>
  );
};

const BelowAddToCartDetailsBase: React.FC<{
  nonReturnableReason: string | undefined;
  brand: IProductPageResponse.IPdpBrand;
  importShipmentInfo: string | undefined;
  infoTexts: IInformationMessage[] | undefined;
  safetyInformation: IDisclaimer[] | undefined;
  promotionLabelText: string | undefined;
  insiderLabel: IInsiderLabel | undefined;
  productAddToCartState: ProductAddToCartState;
  product: IProductPageResponse.IPdpProduct;
}> = ({
  nonReturnableReason,
  brand,
  importShipmentInfo,
  infoTexts,
  safetyInformation,
  promotionLabelText,
  insiderLabel,
  productAddToCartState,
  product,
}) => {
  return (
    <>
      {isLoggedInRetailerOrBrandPreview() ? (
        <>
          <LoggedInOnlyNote
            nonReturnableReason={nonReturnableReason}
            product={product}
            selectedOption={productAddToCartState.selectedOption}
          />
          <ShippingDetailsList
            brand={brand}
            importShipmentInfo={importShipmentInfo}
            infoTexts={infoTexts}
            shippingPromotion={
              productAddToCartState.productPromotionState
                ?.guaranteedFreeShippingPromo
            }
          />
        </>
      ) : null}
      {safetyInformation?.length ? (
        <SafetyInformation disclaimers={safetyInformation} />
      ) : null}
      <PromotionLabel text={promotionLabelText} />
      <InsiderLabelRenderer insiderLabel={insiderLabel} />
      {getSettingEnableRetailerExclusivityAgreements() ? (
        <ExclusivityLabel brandToken={brand.token ?? ""} />
      ) : null}
      <ProductPromoBanners
        promotions={
          productAddToCartState.productPromotionState?.nonPromotionStyleBanners
        }
        position={IInformationMessage.Position.DEFAULT}
      />
      <ProductPromoBanners
        promotions={
          productAddToCartState.productPromotionState?.nonFashionWeekPromos
        }
        position={IInformationMessage.Position.BOTTOM}
      />
    </>
  );
};
const BelowAddToCartDetails = observer(BelowAddToCartDetailsBase);

const BelowAddToCartDetailsUpdateBase: React.FC<{
  nonReturnableReason: string | undefined;
  brand: IProductPageResponse.IPdpBrand;
  importShipmentInfo: string | undefined;
  infoTexts: IInformationMessage[] | undefined;
  promotionLabelText: string | undefined;
  safetyInformation: IDisclaimer[] | undefined;
  productAddToCartState: ProductAddToCartState;
  product: IProductPageResponse.IPdpProduct;
}> = ({
  nonReturnableReason,
  brand,
  importShipmentInfo,
  infoTexts,
  promotionLabelText,
  safetyInformation,
  productAddToCartState,
  product,
}) => {
  return (
    <>
      {isLoggedInRetailerOrBrandPreview() ? (
        <LoggedInOnlyNote
          nonReturnableReason={nonReturnableReason}
          product={product}
          selectedOption={productAddToCartState.selectedOption}
        />
      ) : null}
      {safetyInformation?.length ? (
        <SafetyInformation disclaimers={safetyInformation} />
      ) : null}
      <PromotionLabel text={promotionLabelText} />
      <ProductPromoBanners
        promotions={
          productAddToCartState.productPromotionState?.nonPromotionStyleBanners
        }
        position={IInformationMessage.Position.DEFAULT}
      />
      <ProductPromoBanners
        promotions={
          productAddToCartState.productPromotionState?.nonFashionWeekPromos
        }
        position={IInformationMessage.Position.BOTTOM}
      />
      {isLoggedInRetailerOrBrandPreview() ? (
        <ShippingDetailsList
          brand={brand}
          importShipmentInfo={importShipmentInfo}
          infoTexts={infoTexts}
          shippingPromotion={
            productAddToCartState.productPromotionState
              ?.guaranteedFreeShippingPromo
          }
        />
      ) : null}
    </>
  );
};
const BelowAddToCartDetailsUpdate = observer(BelowAddToCartDetailsUpdateBase);

interface IDisclaimersProps {
  disclaimers?: IDisclaimer[];
}

const SafetyInformation: React.FC<IDisclaimersProps> = ({ disclaimers }) => (
  <>
    {disclaimers?.map((disclaimer) => (
      <>
        <Spacer size="3x" />
        <Typography variant="paragraphSansMedium">
          {disclaimer.title}
        </Typography>
        <Spacer size="1x" />
        <Typography key={disclaimer.message}>{disclaimer.message}</Typography>
      </>
    ))}
    <Spacer size="1x" />
  </>
);

const Disclaimers: React.FC<IDisclaimersProps> = ({ disclaimers }) => (
  <>
    {disclaimers?.length
      ? disclaimers.map((disclaimer) => (
          <React.Fragment key={disclaimer.message}>
            <Spacer size="1x" />
            <Disclaimer disclaimer={disclaimer} />
          </React.Fragment>
        ))
      : null}
  </>
);

interface IInsiderLabelRendererProps {
  insiderLabel: IInsiderLabel | undefined;
}

const InsiderLabelRenderer: React.FC<IInsiderLabelRendererProps> = ({
  insiderLabel,
}) => {
  if (!insiderLabel?.text) {
    return null;
  }
  return (
    <>
      <Spacer height="2x" />
      <InsiderLabel
        insiderLabel={insiderLabel}
        tooltipPosition="left"
        context={IInsiderModalRequest.InsiderModalContext.PDP}
      />
    </>
  );
};

interface IPromotionLabelProps {
  text?: string;
}

const PromotionLabel: React.FC<IPromotionLabelProps> = ({ text }) => {
  if (!text) {
    return null;
  }
  return (
    <>
      <Spacer height="2x" />
      <Typography variant="paragraphSansMedium" color={Decorative.shade.orange}>
        {text}
      </Typography>
    </>
  );
};

const Subheader: React.FC<{ brandCompanyName: string | undefined }> = ({
  brandCompanyName,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { isFaireDirectActiveInLoggedOut } = useFaireDirectBrand();
  const productSubheaderName = getProductSubheaderName(
    {
      brandCompanyName,
      isFaireDirectActiveInLoggedOut,
    },
    strictLocalize
  );

  if (!productSubheaderName) {
    return null;
  }

  return (
    <>
      <Spacer height="2x" />
      <Typography as="h2">{productSubheaderName}</Typography>
    </>
  );
};

const SignUpFromModalCTA: React.FC = () => {
  const { isFaireDirectActiveInLoggedOut } = useFaireDirectBrand();

  if (isFaireDirectActiveInLoggedOut) {
    return (
      <StrictLocalMsg
        defaultMessage="Create a retailer account"
        description={{
          text: "button to create a new retailer account on Faire",
        }}
      />
    );
  } else {
    return (
      <StrictLocalMsg
        defaultMessage="Unlock wholesale price"
        description={{
          text: "button to get access to the wholesale price of a product on Faire",
          img: "https://cdn.faire.com/fastly/85c0f95e52af7b1e57fd3ef68e0538a7cefcdbfa017c5d549df6f5ce618000b1.png",
        }}
      />
    );
  }
};

interface ISignUpEntryPointProps {
  productToken: string | undefined;
  brandToken: string | undefined;
}

const SignUpEntryPoint: React.FC<ISignUpEntryPointProps> = ({
  productToken,
  brandToken,
}) => {
  const settingFaireDirectPDPSignup = useSettingFaireDirectPDPSignup();
  const {
    isFaireDirectSignUpEligible,
    faireDirectSignupRequiredEmailAddressSignUpRoute,
  } = useFaireDirectSignUp();

  const openSignUpModal = () =>
    popSignUp(
      SignUpType.PRODUCT,
      trackSignupPdpMainCtaClick,
      {
        element: SignUpElement.LIST,
        sourceId: "product_details_sign_up_entry_point",
      },
      {
        product: productToken,
        brand: brandToken,
      }
    );

  return (
    <>
      <Spacer height="3x" />
      {settingFaireDirectPDPSignup && isFaireDirectSignUpEligible ? (
        <Button
          linkTo={faireDirectSignupRequiredEmailAddressSignUpRoute}
          onClick={trackSignupPdpMainCtaClick}
        >
          <StrictLocalMsg
            defaultMessage="Sign up for free"
            description={{
              text: "Click this button on the product detail page will go to the Faire Direct sign up flow",
              img: "https://cdn.faire.com/fastly/8c1cc5ff3a22608246c88c648ecc91403f384de02992546f167f92ae5271c671.png",
            }}
          />
        </Button>
      ) : (
        <Button data-test-id="product-sign-up-button" onClick={openSignUpModal}>
          <SignUpFromModalCTA />
        </Button>
      )}

      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Already have a retailer account? <TextLinkFormatting>Sign in</TextLinkFormatting>"
          description={{
            text: "Sign in link if the retailer already has an account",
            img: "https://cdn.faire.com/fastly/9fcbf75098e1be39a96ec6225d292fb8041dba550cf63586be946f292b11492d.png",
          }}
          values={{
            TextLinkFormatting: (chunks) =>
              renderTextLinkFormatting(chunks, productToken),
          }}
        />
      </Typography>
      <Spacer height="2x" />
    </>
  );
};

const renderTextLinkFormatting = (
  chunks: React.ReactNode,
  productToken?: string
) => (
  <EmbeddedLink
    linkTo={getCurrentPath()
      .append({
        signIn: "product",
        product: productToken,
      })
      .toString()}
  >
    {chunks}
  </EmbeddedLink>
);

const useBrandPromotion = (
  brand: IProductPageResponse.IPdpBrand,
  product: IProductPageResponse.IPdpProduct
) => {
  const { isLive } = useMarketStore(["isLive"]);

  return useMemo(() => {
    if (!isLive) {
      return undefined;
    }

    // Markets have done discount matching on brand promotions, so check for that type
    const tieredPromo = brand?.active_promo_collections?.find(
      (activePromoCollection) =>
        activePromoCollection.program ===
        IBrandPromoCollection.Program.BRAND_PROMOTION
    );

    if (!tieredPromo?.promos[0]?.discount_bps) {
      return undefined;
    }

    // Don't show ATS promo if the product is preorderable
    if (
      tieredPromo?.product_ship_availability ===
        IBrandPromoCollection.ProductShipAvailability.ONLY_IMMEDIATE_PRODUCTS &&
      product.purchase_information?.is_preorderable
    ) {
      return undefined;
    }

    // Show highest tier brand promo from the brand, if applicable.
    const promosFromBrand = tieredPromo?.promos;
    if (promosFromBrand) {
      return promosFromBrand[promosFromBrand.length - 1];
    }

    return undefined;
  }, [brand?.active_promo_collections, isLive, product]);
};

export const ProductDetails = observer(ProductDetailsBase);

const Wrapper = styled(Column)`
  // This needs to be 100vw to make sure the brand name truncation works
  width: 100vw;
  padding: ${getSpacing(0, "2x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    width: 100%;
    padding: 0;
  }
`;

const StyledShowroomBadge = styled(ShowroomBadge)`
  margin-bottom: 12px;
`;
